import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Fab,
  Tab,
  Tabs,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Chip,
  Link,
  Stack,
  Button,
  Tooltip
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { statusColors, statusMap } from '../../dashboard/components';
import Flex from '../../../components/base/Flex';
import ContainedButton from '../../../components/base/ContainedButton';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { RiSettings4Fill } from 'react-icons/ri';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getMoreReadableDuration } from '../../../utils/time';
import Zoom from 'react-medium-image-zoom';
import { rgba } from '../../../utils/colors';
import PassedSvg from '../../../components/svg/PassedSvg';
import SkippedSvg from '../../../components/svg/SkippedSvg';
import FailedSvg from '../../../components/svg/FailedSvg';
import ComingSoonBadge from '../../../components/base/ComingSoonBadge';
import { useActiveProject } from '../../../store/projectState';
import { processString } from './StatusBadge';
import TextButton from '../../../components/base/TextButton';
import { formatDate } from '../../../utils/formateDate';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { useTheme } from '@emotion/react';
import { TRACKING_IDS, trackMixPanel } from '../../../mixpanel.constants';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OutlinedButton from '../../../components/base/OutlinedButton';
import { useScandiumMutation } from '../../../data-layer/utils';
import { toast } from 'react-toastify';


const TabPanel = ({ children, value, index }) => {
  return <div hidden={value !== index}>{value === index && <Box>{children}</Box>}</div>;
};

const StatusBadge = ({ status, error }) => {
  const _status = status in statusMap ? statusMap[status] : status;
  const iconMap = {
    passed: <PassedSvg />,
    failed: <FailedSvg />,
    skipped: <SkippedSvg />
  };

  return (
    <Flex
      alignItems={'center'}
      backgroundColor={rgba(statusColors[_status], 15)}
      px={1}
      py={1}
      color={statusColors[_status]}
      sx={{
        border: `1px solid ${statusColors[_status]}`,
        borderRadius: '4px'
      }}>
      {iconMap[_status] || null}
      <Typography fontWeight={500} lineHeight={1}>
        {_status === 'failed' && error ? `Failed: ${error}` : _status}
      </Typography>
    </Flex>
  );
};

const StepDetails = ({
  testTitle,
  event,
  runResult,
  onOpenSettings,
  onClose,
  onNext,
  onPrev,
  handleUpdateEventScreenshot,
  logData
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const activeProject = useActiveProject();
  const readyOnly = activeProject?.read_only;
  const theme = useTheme();

  const handleKeydown = useCallback(
    (event) => {
      if (event.key === 'ArrowRight' && onNext) return onNext();
      if (event.key === 'ArrowLeft' && onPrev) return onPrev();
    },
    [onNext, onPrev]
  );

  const {
    mutateAsync: analyzeStepError,
    isLoading: isAnalyzing,
    data: getHelpResponse
  } = useScandiumMutation(`/projects/${activeProject?.id}/test-cases/analyze`, {
    enabled: !!activeProject?.id,
    onError: (data) => {
      toast.error(data.message);
    }
  });

  const getHelpForStepError = async () => {
    try {
      const payload = {
        title: testTitle,
        error_message: runResult?.error || '',
        step_text: event?.title || '',
        expected_result: '',
        baseline_screenshot: event?.screenshot?.imageData || '',
        result_screenshot: runResult?.result_screenshot?.imageData || ''
      };

      await analyzeStepError(payload);
    } catch (error) {
      console.error('Error analyzing step:', error);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const linkText = processString(runResult?.error);

  const getBackgroundColor = (level) => {
    switch (level) {
      case 'DEBUG':
        return '#FFECB3';
      case 'INFO':
        return '#EEE';
      case 'ERROR':
        return '#FFCDD2';
      case 'SEVERE':
        return '#FFCDD2';
      default:
        return '#FFFFFF';
    }
  };

  const getIcon = (level) => {
    switch (level) {
      case 'ERROR':
        return <ErrorOutlineIcon sx={{ color: '#000' }} />;
      case 'INFO':
        return <InfoOutlinedIcon sx={{ color: '#000' }} />;
      case 'DEBUG':
        return <BugReportOutlinedIcon sx={{ color: '#000' }} />;
      case 'SEVERE':
        return <BugReportOutlinedIcon sx={{ color: '#000' }} />;
      default:
        return <InfoOutlinedIcon sx={{ color: '#000' }} />;
    }
  };

  const getUrl = (screenshot, status) => {
    if (!screenshot || !screenshot.imageData) {
      return '/images/blank-img.png';
    }

    if (typeof status !== 'undefined') {
      if (status !== 'success') {
        return screenshot.imageData;
      }

      if (
        !screenshot.width ||
        !screenshot.height ||
        !screenshot.offsetLeft ||
        !screenshot.offsetTop
      ) {
        return screenshot.imageData;
      }
    }

    try {
      if (!screenshot.imageDimensions || !screenshot.screenDimensions) return screenshot.imageData;

      const xRatio = screenshot.imageDimensions?.width / screenshot.screenDimensions?.width;
      const yRatio = screenshot.imageDimensions?.height / screenshot.screenDimensions?.height;

      if (!xRatio || !yRatio) {
        return screenshot.imageData;
      }

      const w = Math.round(screenshot.width * xRatio);
      const h = Math.round(screenshot.height * yRatio);
      const l = Math.round(screenshot.offsetLeft * xRatio);
      const t = Math.round(screenshot.offsetTop * xRatio);

      if (screenshot.imageData.includes('cloudinary')) {
        const encodedUrl = encodeURIComponent(screenshot.imageData);
        return `https://picstro.getscandium.com/media/${encodedUrl}?spotlight=x_${l},y_${t},w_${w},h_${h},darkness_0.5`;
      }

      return `${screenshot.imageData}?spotlight=x_${l},y_${t},w_${w},h_${h},darkness_0.5`;
    } catch (error) {
      return screenshot.imageData;
    }
  };

  return (
    <Box flex={1} mr={2} mb={4}>
      <Flex alignItems={'flex-end'} justifyContent={'space-between'}>
        <Box>
          {/* Breadcrumb with integrated back button */}
          <Flex alignItems="center" gap={1}>
            <Button
              variant="text"
              size="small"
              onClick={onClose}
              startIcon={<KeyboardBackspaceRoundedIcon sx={{ color: '#6A6A6A' }} />}
              sx={{
                color: 'inherit',
                p: 0,
                minWidth: 'auto',
                textTransform: 'none',
                fontSize: '1.1rem',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}>
              Back
            </Button>
            <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              /
            </Typography>
            <Typography
              color="primary"
              sx={{
                textAlign: 'left',
                fontSize: '1.1rem',
                fontWeight: 500
              }}>
              {testTitle} / {event.title}
            </Typography>
          </Flex>

          {!!runResult && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{
                mt: 2,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1
              }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ minWidth: 180 }}>
                  <StatusBadge status={runResult.status} error={runResult.error} />
                </Box>

                {runResult.status === 'error' && (
                  <Chip
                    component={Link}
                    icon={<HelpOutlineIcon fontSize="small" />}
                    label="Get Help"
                    clickable
                    size="small"
                    onClick={() => {
                      trackMixPanel(TRACKING_IDS.GET_HELP_CLICKED, {
                        position: 'Step Details'
                      });
                    }}
                    href={`https://docs.getscandium.com/why-did-my-test-fail#${linkText}`}
                    target="_blank"
                    sx={{
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.action.hover,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected
                      }
                    }}
                  />
                )}
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'flex-start', sm: 'center' }
                }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Time Taken
                </Typography>
                <Chip
                  label={getMoreReadableDuration(runResult.time_taken) || '0ms'}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                    fontWeight: 500
                  }}
                />
              </Box>
            </Stack>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {runResult?.status === 'error' && (
            <Tooltip title={'Get AI suggestions to fix this step'}>
              <OutlinedButton
                startIcon={<AutoAwesomeIcon fontSize={'1rem'} />}
                disabled={!!readyOnly}
                isLoading={isAnalyzing}
                onClick={getHelpForStepError}>
                Get Help
              </OutlinedButton>
            </Tooltip>
          )}

          {!!onOpenSettings && (
            <ContainedButton
              startIcon={<RiSettings4Fill fontSize={'1rem'} />}
              disabled={!!readyOnly}
              onClick={onOpenSettings}>
              Settings
            </ContainedButton>
          )}
        </Box>
      </Flex>

      {runResult?.variableValue && (
        <Flex sx={{ my: 1 }}>
          <Typography>Variable value - </Typography>
          <Box
            sx={{
              py: 0.5,
              px: 1,
              backgroundColor: '#1958B8',
              borderRadius: '24px',
              width: 'max-content'
            }}>
            <Typography
              sx={{ color: '#FFF', textTransform: 'capitalize', lineHeight: 1 }}
              fontSize={'0.7rem'}>
              {runResult?.variableValue}
            </Typography>
          </Box>
        </Flex>
      )}

      {getHelpResponse?.data?.message && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: '8px',
            backgroundColor:
              theme.palette.mode === 'light' ? '#F5FAFF' : theme.palette.background.paper,
            border: `1px solid ${
              theme.palette.mode === 'light' ? '#D6E7FF' : theme.palette.divider
            }`,
            boxShadow: theme.palette.mode === 'light' ? 'none' : '0px 2px 4px rgba(0,0,0,0.1)'
          }}>
          <Flex alignItems={'flex-start'} columnGap={4}>
            <Box>
              <AutoAwesomeIcon fontSize={'large'} color={'primary'} />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                AI Assistant
              </Typography>
              <Box
                sx={{
                  '& h3': {
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mt: 2,
                    mb: 1
                  },
                  '& ul': {
                    pl: 3,
                    mb: 2
                  },
                  '& li': {
                    mb: 1
                  },
                  '& p': {
                    mb: 2
                  }
                }}>
                <ReactMarkdown>{getHelpResponse.data.message}</ReactMarkdown>
              </Box>
            </Box>
          </Flex>
        </Box>
      )}

      <Box
        sx={{
          px: 3,
          py: 3,
          mt: 2,
          borderRadius: '16px',
          borderColor: theme.palette.table.mobileOutline,
          borderWidth: '2px',
          borderStyle: 'solid'
        }}>
        <Tabs
          value={selectedTab}
          onChange={(event, value) => setSelectedTab(value)}
          indicatorColor={'secondary'}
          sx={{
            borderBottom: `3px solid ${theme.palette.table.outline}`
          }}
          TabIndicatorProps={{
            sx: { height: 3 }
          }}>
          <Tab label={'Baseline'} sx={{ textTransform: 'none', textAlign: 'left' }} />
          {!!runResult && (
            <Tab label={'Results'} sx={{ textTransform: 'none', textAlign: 'left' }} />
          )}
          {!!runResult && (
            <Tab
              label={<Flex>Console Log</Flex>}
              sx={{ textTransform: 'none', textAlign: 'right' }}
            />
          )}
          {!!runResult && (
            <Tab
              disabled
              label={
                <Flex>
                  Network Log
                  <ComingSoonBadge />
                </Flex>
              }
              sx={{ textTransform: 'none', textAlign: 'right' }}
            />
          )}
        </Tabs>
        <TabPanel value={!!runResult ? selectedTab : 0} index={0}>
          <Flex justifyContent={'center'} sx={{ py: 4 }}>
            <Box sx={{ maxWidth: '50%' }}>
              <Zoom>
                <Box
                  as={'img'}
                  alt={
                    event.screenshot?.imageData
                      ? 'Page screenshot'
                      : 'No screenshots available for this step'
                  }
                  src={getUrl(event.screenshot)}
                  sx={{ width: '100%' }}
                />
              </Zoom>
            </Box>
          </Flex>
        </TabPanel>

        {!!runResult && (
          <TabPanel value={selectedTab} index={1}>
            <Flex
              justifyContent={'space-around'}
              sx={{ py: 4, maxWidth: '100%' }}
              alignItems={'flex-start'}
              py={2}>
              <Box sx={{ flex: 1, maxWidth: '50%' }}>
                <Box
                  sx={{
                    borderTopRightRadius: '8px',
                    px: 2,
                    py: 0.5,
                    bgcolor: '#24C3D9',
                    color: '#FFF',
                    width: 'max-content'
                  }}>
                  Baseline
                </Box>
                <Zoom>
                  <Box as={'img'} src={getUrl(event.screenshot)} sx={{ width: '100%' }} />
                </Zoom>
              </Box>
              <Box sx={{ flex: 1, maxWidth: '50%' }}>
                <Flex justifyContent={'space-between'}>
                  <Box
                    sx={{
                      borderTopRightRadius: '8px',
                      px: 2,
                      py: 0.5,
                      bgcolor: '#24C3D9',
                      color: '#FFF',
                      width: 'max-content'
                    }}>
                    Result
                  </Box>
                  {runResult?.result_screenshot?.imageData && (
                    <TextButton
                      onClick={() =>
                        handleUpdateEventScreenshot(
                          runResult.step_id,
                          runResult.result_screenshot.imageData
                        )
                      }>
                      Set as Baseline
                    </TextButton>
                  )}
                </Flex>
                <Zoom>
                  <Box
                    as={'img'}
                    src={getUrl(runResult.result_screenshot, runResult.status)}
                    sx={{ width: '100%' }}
                  />
                </Zoom>
              </Box>
            </Flex>
          </TabPanel>
        )}

        {!!runResult && (
          <TabPanel value={selectedTab} index={2}>
            <Box sx={{ py: 2 }}>
              <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Level</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logData?.[0]?.value?.map((entry, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: getBackgroundColor(entry.level),
                          '&:hover': { backgroundColor: getBackgroundColor(entry.level) },
                          border: 'none',
                          borderBottom: '2px solid #FFF'
                        }}>
                        <TableCell
                          sx={{
                            border: 'none'
                          }}>
                          <Flex>
                            {getIcon(entry.level)}
                            <Typography variant="body2" color={'black'}>
                              {entry.level}
                            </Typography>
                          </Flex>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={'black'}>
                            {entry.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={'black'}>
                            {formatDate(entry.timestamp)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        )}
      </Box>

      <Flex justifyContent={'space-between'} flex={1} mt={2}>
        <Fab
          onClick={onPrev}
          disabled={!onPrev}
          size={'small'}
          color={'gray'}
          elevation={0}
          sx={{
            width: '28px',
            height: '28px',
            minHeight: '28px',
            boxShadow: 0,
            color: theme.palette.text.black_grey
          }}
          aria-label={'Next event'}>
          <ChevronLeft sx={{ color: theme.palette.svg.primary }} />
        </Fab>

        <Fab
          onClick={onNext}
          disabled={!onNext}
          size={'small'}
          color={'grey'}
          sx={{
            width: '28px',
            height: '28px',
            minHeight: '28px',
            boxShadow: 0,
            color: theme.palette.text.black_grey
          }}
          aria-label={'Previous event'}>
          <ChevronRight sx={{ color: theme.palette.svg.primary }} />
        </Fab>
      </Flex>
    </Box>
  );
};

export default StepDetails;
